'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { getBaseImageUrl, getMerchantHref, getProductDetailHref, discardHTMLTags, getCurrencySymbol, calculateOfferDuration } from '@/constants/hooks'
import { CalendarDays, ArrowRight, CheckCircle, Tag, Clock, Sparkles } from 'lucide-react'
import Theme18OfferLink from './Theme18OfferLink'

const C = { PRIMARY: '#F8F246', SECONDARY: '#8C5826', YELLOW: '#FF9934', BROWN: '#8C5826', LIGHT: '#fffbf0' }

interface Props {
    offers: any[]
    eventName?: string
    mer_slug: string
    mer_slug_type: string
    domain: string
    companyId: string
}

const PetCareEventsSection = ({ offers, eventName, mer_slug, mer_slug_type, domain, companyId }: Props) => {
    const currency = getCurrencySymbol()
    if (!offers?.length) return null

    const displayOffers = offers.slice(0, 6)

    return (
        <section className="py-8 md:py-10 px-4" style={{ background: '#fffaf5' }}>
            <div className="max-w-7xl mx-auto">
                {/* Banner Header */}
                <div className="relative rounded-2xl overflow-hidden mb-8 px-5 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    style={{ background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffb347 100%)` }}>
                    <div className="flex items-center gap-3">
                        <div>
                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-1"
                                style={{ background: 'rgba(255,255,255,0.2)', color: C.BROWN }}>
                                <CalendarDays size={10} /> Limited Time
                            </div>
                            <h2 className="text-xl font-black" style={{ color: C.BROWN }}>
                                <span style={{ color: '#1a1a1a' }}>{eventName || 'Special'}</span>{' '}
                                <span style={{ color: '#8C5826' }}>Offers</span>
                            </h2>
                        </div>
                    </div>
                    <Link href="/all-products"
                        className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-extrabold text-xs sm:text-sm transition-all duration-300 hover:scale-105 shadow-sm whitespace-nowrap flex-shrink-0"
                        style={{
                            background: '#F8F246',
                            color: '#8C5826',
                            border: '1px solid rgba(140,88,38,0.15)',
                            boxShadow: '0 4px 16px rgba(248, 242, 70, 0.35)',
                        }}>
                        <span>View All Offers</span>
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    {displayOffers.map((item, idx) => {
                        const offer = item.offer
                        const merchant = item.merchant
                        const imageSrc = offer?.offer_type?.name === 'product'
                            ? getBaseImageUrl(domain, offer?.product_image, '')
                            : getBaseImageUrl(domain, merchant?.merchant_logo, '')
                        const href = getMerchantHref(merchant, mer_slug, mer_slug_type)
                        const detailUrl = offer?.slug && offer?.is_detail === 1
                            ? getProductDetailHref(merchant, mer_slug_type, offer.slug) : null
                        const origPrice = offer?.original_price ? parseFloat(offer.original_price) : 0
                        const salePrice = offer?.sale_price ? parseFloat(offer.sale_price) : 0
                        const isCoupon = !!offer?.coupon_code

                        return (
                            <article key={idx}
                                className="group rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 flex flex-col"
                                style={{ background: '#ffffff', border: '1px solid #f3f4f6', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
                                <div className="relative h-36 flex items-center justify-center p-4"
                                    style={{ background: `linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)` }}>
                                    {idx < 3 && (
                                        <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold"
                                            style={{ background: '#8C5826', color: '#ffffff' }}>
                                            <Sparkles size={8} /> Top Deal
                                        </div>
                                    )}
                                    {isCoupon && (
                                        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold"
                                            style={{ background: `${C.PRIMARY}20`, color: C.BROWN, border: `1px dashed ${C.PRIMARY}` }}>
                                            <Tag size={8} /> COUPON
                                        </div>
                                    )}
                                    <Image src={imageSrc} alt={discardHTMLTags(offer?.offer_title) || 'Pet product'} fill
                                        className="object-contain group-hover:scale-110 transition-transform duration-300"
                                        sizes="(max-width: 768px) 100vw, 33vw" />
                                </div>

                                <div className="p-4 flex flex-col flex-1">
                                    <Link href={href} className="text-[10px] font-semibold mb-1 block hover:opacity-70 transition-opacity" style={{ color: '#8C5826' }}>
                                        {merchant?.merchant_name}
                                    </Link>
                                    {detailUrl ? (
                                        <Link href={detailUrl} className="block">
                                            <h3 className="text-sm font-bold line-clamp-2 mb-2 transition-colors group-hover:text-[#8C5826]" style={{ color: '#1a1a1a' }}>
                                                {discardHTMLTags(offer?.offer_title)}
                                            </h3>
                                        </Link>
                                    ) : (
                                        <Theme18OfferLink
                                            unique_id={offer?.unique_id}
                                            outUrl={offer?.url || href}
                                            merchantHref={href}
                                            domain={domain}
                                            couponCode={offer?.coupon_code || undefined}
                                            customClass="block"
                                        >
                                            <h3 className="text-sm font-bold line-clamp-2 mb-2 transition-colors hover:text-[#8C5826]" style={{ color: '#1a1a1a' }}>
                                                {discardHTMLTags(offer?.offer_title)}
                                            </h3>
                                        </Theme18OfferLink>
                                    )}

                                    {salePrice > 0 && (
                                        <div className="flex items-baseline gap-2 mb-3">
                                            <span className="text-lg font-black" style={{ color: '#1a1a1a' }}>{currency}{salePrice.toFixed(2)}</span>
                                            {origPrice > 0 && (
                                                <span className="text-xs line-through" style={{ color: '#9ca3af' }}>{currency}{origPrice.toFixed(2)}</span>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between">
                                        <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: '#8C5826' }}>
                                            <CheckCircle size={12} /> Verified
                                        </span>
                                        {offer?.end_date && (
                                            <span className="flex items-center gap-1 text-xs" style={{ color: '#9ca3af' }}>
                                                <span suppressHydrationWarning><Clock size={10} /> {calculateOfferDuration(offer.end_date)}</span>
                                            </span>
                                        )}
                                    </div>

                                    <Theme18OfferLink
                                        unique_id={offer?.unique_id}
                                        outUrl={offer?.url || href}
                                        merchantHref={href}
                                        domain={domain}
                                        couponCode={offer?.coupon_code || undefined}
                                        customClass="mt-auto pt-4 w-full block"
                                    >
                                        <div
                                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold transition-all duration-300 group-hover:shadow-lg"
                                            style={{ background: '#8C5826', color: '#ffffff' }}>
                                            {isCoupon ? 'Get Code' : 'Shop Now'}
                                        </div>
                                    </Theme18OfferLink>
                                </div>
                            </article>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

export default PetCareEventsSection